import React, { useEffect, useState } from 'react';
import { askOrbitAction } from '../../components/AskOrbit';
import { useSearchParams } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { AaiRequest, AaiSkill, DepartmentQuickWin } from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, FilterChips, ListRow, Loading, PageBody, PageHead, Panel, PanelHead, RouteMark,
  StatusChip, Tabs,
} from '../../components/aa';
import { useRecordLinks } from '../../hooks/useRecordLinks';
import { brief, isReadyToUse } from '../owner/WorkRows';
import { nextFor, ownerOf, stateFor } from '../partner/work';
import { relativeTime } from '../partner/format';
import { departmentName, plural, useDepartment } from './useDepartment';

/*
 * Projects, for the Department Head: the approved work that belongs to or
 * affects their department. Quick Wins are used and reviewed in the portal and
 * open their record. Delivery Projects open the Project record; a Workspace
 * opens from there only for named members, and the record says so.
 */

type WorkFilter = 'active' | 'needs_me' | 'in_progress' | 'waiting' | 'ready' | 'all';
const FILTERS: Array<{ id: WorkFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'needs_me', label: 'Needs me' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'ready', label: 'Ready to use' },
  { id: 'all', label: 'All' },
];

const MATCH: Record<WorkFilter, (r: AaiRequest) => boolean> = {
  active: (r) => r.status !== 'declined',
  needs_me: (r) => r.status === 'pending' || r.status === 'needs_info',
  in_progress: (r) => !isReadyToUse(r) && (r.status === 'approved' || r.status === 'dept_approved'),
  waiting: (r) => r.status === 'dept_approved',
  ready: isReadyToUse,
  all: () => true,
};

const stateWord = (r: AaiRequest): string => {
  if (isReadyToUse(r)) return 'Ready to use';
  if (r.status === 'approved') return 'Workspace ready · In build';
  if (r.status === 'dept_approved') return 'Cleared · awaiting company approval';
  if (r.status === 'needs_info') return 'Detail requested';
  if (r.status === 'declined') return 'Declined';
  return 'Waiting on your review';
};

const DeliveryRow: React.FC<{ request: AaiRequest; onOpen: (id: string) => void }> = ({ request, onOpen }) => {
  let status = stateFor(request);
  if (isReadyToUse(request)) status = 'ready_short';
  let when = `Raised ${relativeTime(request.created_at)}`;
  if (request.decided_at) when = `Decided ${relativeTime(request.decided_at)}`;
  return (
  <ListRow
    mark={<span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-[13px] font-bold text-white">D</span>}
    title={request.title}
    badge={<StatusChip status={status} />}
    desc={brief(request.detail)}
    foot={`${stateWord(request)} · ${when}`}
    meta={[
      { label: 'Asked by', value: request.requester_name || request.requester_email || 'Unknown' },
      { label: 'Owner', value: ownerOf(request), foot: request.department || 'Whole company' },
      { label: 'Next', value: nextFor(request) },
    ]}
    metaWidth={[120, 200, 190]}
    onClick={() => onOpen(request.id)}
    arrow
  />
  );
};

const QuickWinRow: React.FC<{ win: DepartmentQuickWin; onOpen: (id: string) => void }> = ({ win, onOpen }) => {
  let scope = 'Company';
  if (win.scope === 'team') scope = 'Team';
  let setup = 'Published';
  if (win.use_case_id) setup = 'Already approved';
  let usage = 'Usage protected under the reporting threshold';
  if (win.usage_pct !== null) usage = `${win.usage_pct}% of the team using it`;
  return (
    <ListRow
      mark={<RouteMark kind="quick_win" size="lg" />}
      title={win.name}
      desc={brief(win.problem)}
      foot={usage}
      meta={[
        { label: 'Scope', value: scope },
        { label: 'For', value: win.department || 'Whole company' },
        { label: 'Setup', value: setup },
      ]}
      right={<StatusChip status="ready_short" />}
      onClick={() => onOpen(win.id)}
      arrow
    />
  );
};

const DepartmentProjectsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'all';
  const openParam = params.get('open');
  const overview = useDepartment();
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load Quick Wins.');
  const requests = useAsync<{ scope: string; requests: AaiRequest[] }>(() => aaiApi.listRequests(), 'Could not load department work.');
  const [filter, setFilter] = useState<WorkFilter>('active');
  const links = useRecordLinks();
  const setOpenProject = links.project;
  const setOpenSkill = (skill: AaiSkill) => links.quickWin(skill.id);

  useEffect(() => {
    if (openParam) setOpenProject(openParam, true);
  }, [openParam, setOpenProject]);

  const setTab = (id: string) => setParams({ tab: id }, { replace: true });

  if (overview.loading) return <PageBody><Loading label="Loading department projects" /></PageBody>;
  if (overview.error || !overview.data) return <PageBody><ErrorNote message={overview.error || 'Something went wrong.'} /></PageBody>;

  const data = overview.data;
  const name = departmentName(data);
  const all = (requests.data?.requests || []).filter((r) => (r.department || '').toLowerCase() === (data.department.name || '').toLowerCase());
  const rows = all.filter(MATCH[filter]);
  const active = all.filter((r) => r.status === 'approved' && !isReadyToUse(r));
  const showQuick = tab === 'all' || tab === 'quick_wins';
  const showDelivery = tab === 'all' || tab === 'delivery';

  const openQuickWin = (id: string) => {
    const skill = (skills.data?.skills || []).find((s) => s.id === id) || null;
    if (skill) {
      setOpenSkill(skill);
      return;
    }
    setOpenProject(null);
  };

  return (
    <PageBody>
      <PageHead
        align="center"
        title="Projects"
        actions={askOrbitAction()}
      />

      <Tabs
        tabs={[
          { id: 'all', label: 'All projects', count: data.quick_wins.length + all.filter((r) => r.status !== 'declined').length },
          { id: 'quick_wins', label: 'Quick Wins', count: data.quick_wins.length },
          { id: 'delivery', label: 'Delivery Projects', count: all.filter((r) => r.status !== 'declined' && !isReadyToUse(r)).length },
        ]}
        active={tab}
        onChange={setTab}
        label="Project type"
      />

      {showQuick && (
        <Panel className="mb-6">
          <PanelHead
            kicker="Project type · Quick Win"
            title="Focused projects for your team"
            desc="A focused result with limited setup, used and reviewed directly in the portal."
            action={tab === 'all' && <Action size="sm" variant="quiet" onClick={() => setTab('quick_wins')}>See all Quick Wins</Action>}
          />
          {data.quick_wins.length === 0 && (
            <EmptyState title="No Quick Wins for your team yet" reason="A Quick Win appears here once a capability is published for the company or for your department." />
          )}
          <Divided>
            {data.quick_wins.map((w) => <QuickWinRow key={w.id} win={w} onOpen={openQuickWin} />)}
          </Divided>
        </Panel>
      )}

      {showDelivery && (
        <>
          <div className="mb-3.5 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Project type · Delivery Project</div>
              <h2 className="m-0 text-[20px] font-bold leading-[28px] tracking-[-0.005em] text-ink">{plural(active.length, 'Workspace ready to continue', 'Workspaces ready to continue')}</h2>
              <p className="m-0 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">A larger change that needs shared ownership, scoped delivery, coordinated work, and governed release.</p>
            </div>
            {tab === 'all' && <Action size="sm" variant="quiet" onClick={() => setTab('delivery')}>See all Delivery Projects</Action>}
          </div>

          <Panel className="mb-5" pad="lg">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
              <div className="lg:w-[38%]">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Active workspaces</div>
                <div className="mt-2 text-[28px] font-bold leading-[34px] tracking-[-0.015em] text-ink">{active.length} ready to continue</div>
                <p className="m-0 mt-2 text-[12.5px] leading-[19px] text-ink-dim">Opening a Workspace needs a named membership on that Project. The record shows yours.</p>
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                {active.length === 0 && <div className="rounded-card border border-line bg-white px-5 py-4 text-[13px] text-ink-dim">No Delivery Project is in build for {name} yet.</div>}
                {active.slice(0, 3).map((r) => {
                  let kicker = 'Building · Delivery Project';
                  if (ownerOf(r) !== 'Not yet named') kicker = 'Building · Owner named';
                  return (
                  <button type="button" key={r.id} onClick={() => setOpenProject(r.id)} className="flex items-center gap-3.5 rounded-card border border-line bg-white px-4 py-3 text-left transition-colors hover:bg-canvas">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-[13px] font-bold text-white">D</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{kicker}</span>
                      <span className="block truncate text-[13.5px] font-semibold text-ink">{r.title}</span>
                      <span className="block truncate text-[12px] text-ink-dim">{nextFor(r)}</span>
                    </span>
                    <span className="shrink-0 text-[12.5px] font-semibold text-ink">Open Workspace →</span>
                  </button>
                  );
                })}
              </div>
            </div>
          </Panel>

          <Panel>
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-6">
              <FilterChips tabs={FILTERS} active={filter} onChange={(id) => setFilter(id as WorkFilter)} label="Work state" />
              <span className="text-[12px] text-ink-soft">{rows.length} in this view</span>
            </div>
            {requests.loading && <Loading label="Loading department work" />}
            {requests.error && <div className="px-6 py-5"><ErrorNote message={requests.error} /></div>}
            {!requests.loading && !requests.error && rows.length === 0 && (
              <EmptyState title="Nothing is waiting here" reason="Change the filter. Work arrives when someone in your department asks Orbit for something." />
            )}
            <Divided>
              {rows.map((r) => <DeliveryRow key={r.id} request={r} onOpen={setOpenProject} />)}
            </Divided>
            <div className="flex items-center gap-2.5 px-5 py-4 text-[12.5px] leading-[19px] text-ink-dim md:px-6">
              <Chip tone="private" dot>Named members only</Chip>
              Project summary, Workspace membership and source access are three separate permissions. Workspace membership is not recorded on the platform yet, so Open Workspace explains itself in the record.
            </div>
          </Panel>
        </>
      )}

    </PageBody>
  );
};

export default DepartmentProjectsPage;
